'use client'

import React, { useState, useRef, useCallback, useEffect, useId } from 'react'
import Icon from '../../atoms/Icon/Icon.component'
import * as S from './FilterSelect.styles'
import { useTranslations } from 'next-intl'
import { ColorSchemeTypes } from '../../../types/ColorScheme'

export type FilterSelectProps = {
  colorScheme: ColorSchemeTypes
  label: string
  options: string[]
  selectedValues: string[]
  onToggle: (value: string) => void
  onClear: () => void
}

function FilterSelect({
  colorScheme,
  label,
  options,
  selectedValues,
  onToggle,
  onClear,
}: FilterSelectProps) {
  const t = useTranslations('resourceHub')
  const [isOpen, setIsOpen] = useState(false)
  const wrapperRef = useRef<HTMLDivElement>(null)
  const triggerRef = useRef<HTMLButtonElement>(null)
  const baseId = useId()
  const listId = `${baseId}-panel`
  const triggerId = `${baseId}-trigger`
  const filterLabel = t(`filters.${label}.label`)

  const allSelected = options.length > 0 && options.every((opt) => selectedValues.includes(opt))

  const handleToggleAll = useCallback(() => {
    if (allSelected) {
      onClear()
    } else {
      options.forEach((opt) => {
        if (!selectedValues.includes(opt)) {
          onToggle(opt)
        }
      })
    }
  }, [allSelected, onClear, options, selectedValues, onToggle])

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target as Node)) {
        setIsOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  useEffect(() => {
    if (!isOpen) return

    function handleFocusIn(event: FocusEvent) {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target as Node)) {
        setIsOpen(false)
      }
    }

    document.addEventListener('focusin', handleFocusIn)
    return () => document.removeEventListener('focusin', handleFocusIn)
  }, [isOpen])

  useEffect(() => {
    if (!isOpen) return

    function handleEscape(event: KeyboardEvent) {
      if (event.key === 'Escape') {
        event.stopPropagation()
        setIsOpen(false)
        triggerRef.current?.focus()
      }
    }

    document.addEventListener('keydown', handleEscape)
    return () => document.removeEventListener('keydown', handleEscape)
  }, [isOpen])

  return (
    <S.FilterSelectWrapper ref={wrapperRef}>
      <S.FilterSelectTrigger
        ref={triggerRef}
        id={triggerId}
        type="button"
        $isOpen={isOpen}
        $hasSelection={selectedValues.length > 0}
        onClick={() => setIsOpen((prev) => !prev)}
        colorScheme={colorScheme}
        aria-expanded={isOpen}
        aria-controls={listId}
        aria-haspopup="true"
      >
        <Icon name={label} aria-hidden />
        {filterLabel}
        {selectedValues.length > 0 && (
          <S.FilterSelectSelectedCount colorScheme={colorScheme} aria-hidden>
            {selectedValues.length}
          </S.FilterSelectSelectedCount>
        )}
        <Icon name="chevron-down" aria-hidden />
      </S.FilterSelectTrigger>

      {isOpen && (
        <S.FilterSelectDropdown id={listId} role="region" aria-label={filterLabel}>
          <S.FilterSelectHeader>
            <S.FilterSelectTitle>{filterLabel}</S.FilterSelectTitle>
            <S.FilterSelectClear type="button" onClick={onClear}>
              {t('filters.clear')}
            </S.FilterSelectClear>
          </S.FilterSelectHeader>

          <S.FilterSelectOption as="label">
            <S.FilterSelectCheckboxInput checked={allSelected} onChange={handleToggleAll} />
            <S.FilterSelectCheckbox aria-hidden $checked={allSelected} />
            <S.FilterSelectOptionLabel>{t('filters.selectAll')}</S.FilterSelectOptionLabel>
          </S.FilterSelectOption>

          <S.FilterSelectDivider />

          {options.map((option) => {
            const checked = selectedValues.includes(option)
            const optionLabel = t(`filters.${label}.options.${option}`)
            return (
              <S.FilterSelectOption key={option} as="label">
                <S.FilterSelectCheckboxInput checked={checked} onChange={() => onToggle(option)} />
                <S.FilterSelectCheckbox aria-hidden $checked={checked} />
                <Icon name={option} aria-hidden />
                <S.FilterSelectOptionLabel>{optionLabel}</S.FilterSelectOptionLabel>
              </S.FilterSelectOption>
            )
          })}
        </S.FilterSelectDropdown>
      )}
    </S.FilterSelectWrapper>
  )
}

export default FilterSelect
